import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import {
  type OpNotificationInstance,
  useOpNotificationApi,
} from 'new-components/OpAppScaffold/components/OpNotificationProvider';

import { notificationService } from './notificationService';

const IntercomNotificationContext = createContext<
  OpNotificationInstance | undefined
>(undefined);

const NOTICE_SELECTOR = '.ant-notification-notice-wrapper';

const observeTransformTransitions = (
  container: HTMLElement,
  measure: () => void,
) => {
  let frameId = 0;
  const transitioningNotices = new Set<EventTarget>();
  const tick = () => {
    for (const notice of transitioningNotices) {
      if (!(notice instanceof Node) || !container.contains(notice)) {
        transitioningNotices.delete(notice);
      }
    }
    measure();
    frameId = transitioningNotices.size ? requestAnimationFrame(tick) : 0;
  };
  const handleRun = (event: TransitionEvent) => {
    if (event.propertyName !== 'transform' || !event.target) return;
    transitioningNotices.add(event.target);
    if (!frameId) frameId = requestAnimationFrame(tick);
  };
  const handleDone = (event: TransitionEvent) => {
    if (event.propertyName !== 'transform' || !event.target) return;
    transitioningNotices.delete(event.target);
    measure();
  };

  container.addEventListener('transitionrun', handleRun);
  container.addEventListener('transitionend', handleDone);
  container.addEventListener('transitioncancel', handleDone);
  return () => {
    cancelAnimationFrame(frameId);
    container.removeEventListener('transitionrun', handleRun);
    container.removeEventListener('transitionend', handleDone);
    container.removeEventListener('transitioncancel', handleDone);
  };
};

const syncResizeObservers = (
  container: HTMLElement,
  observers: Map<Element, ResizeObserver>,
  onResize: () => void,
) => {
  const notices = new Set(container.querySelectorAll(NOTICE_SELECTOR));
  for (const [notice, observer] of observers) {
    if (!notices.has(notice)) {
      observer.disconnect();
      observers.delete(notice);
    }
  }
  for (const notice of notices) {
    if (!observers.has(notice)) {
      const observer = new ResizeObserver(onResize);
      observer.observe(notice);
      observers.set(notice, observer);
    }
  }
  onResize();
};

const observeIntercomStackHeight = (container: HTMLElement) => {
  let frameId = 0;
  const noticeObservers = new Map<Element, ResizeObserver>();

  const measure = () => {
    const notices = Array.from(
      container.querySelectorAll<HTMLElement>(NOTICE_SELECTOR),
    );
    if (!notices.length) {
      notificationService.setIntercomStackHeight(0);
      return;
    }
    const rectangles = notices.map((notice) => notice.getBoundingClientRect());
    notificationService.setIntercomStackHeight(
      Math.ceil(
        Math.max(...rectangles.map(({ bottom }) => bottom)) -
          Math.min(...rectangles.map(({ top }) => top)),
      ),
    );
  };

  const scheduleMeasure = () => {
    if (!frameId) {
      frameId = requestAnimationFrame(() => {
        frameId = 0;
        measure();
      });
    }
  };

  const syncNoticeObservers = () =>
    syncResizeObservers(container, noticeObservers, scheduleMeasure);

  const mutationObserver = new MutationObserver(syncNoticeObservers);
  mutationObserver.observe(container, {
    attributes: true,
    attributeFilter: ['class', 'style'],
    childList: true,
    subtree: true,
  });
  const stopObservingTransitions = observeTransformTransitions(
    container,
    measure,
  );
  syncNoticeObservers();

  return () => {
    cancelAnimationFrame(frameId);
    mutationObserver.disconnect();
    stopObservingTransitions();
    for (const observer of noticeObservers.values()) observer.disconnect();
    notificationService.setIntercomStackHeight(0);
  };
};

/**
 * Dedicated antd notification instance for intercom call notifications.
 * Rendered in its own bottomRight stack so intercom notifications never
 * collapse with alarms, toasts, or UI notifications.
 *
 * The stack renders into a provider-owned container so its rendered height
 * can be measured and reported to the notificationService; the general
 * notification stack uses that height to stay above this stack even when
 * multiple intercom calls expand it.
 */
export const IntercomNotificationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [api, contextHolder] = useOpNotificationApi({
    placement: 'bottomRight',
    getContainer: () => containerRef.current ?? document.body,
    // collapse multiple intercom notices so the resting stack stays compact;
    // the measured height still accounts for hover expansion
    stack: { threshold: 1 },
  });

  const contextValue = useMemo(() => api, [api]);

  useEffect(() => {
    if (containerRef.current) {
      return observeIntercomStackHeight(containerRef.current);
    }
    return undefined;
  }, []);

  return (
    <IntercomNotificationContext.Provider value={contextValue}>
      <div ref={containerRef} />
      {contextHolder}
      {children}
    </IntercomNotificationContext.Provider>
  );
};

/**
 * Returns the intercom-dedicated notification API, or undefined when the
 * provider is not mounted. Callers should fall back to the general
 * notification API when this returns undefined.
 */
export const useIntercomNotificationOptional = ():
  | OpNotificationInstance
  | undefined => useContext(IntercomNotificationContext);
